// src/context/ReserveContext.jsx
import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import { getReserves } from "../api/reserves";

const normalizeRoomFromApi = (room) => {
  return {
    ...room,
    amenities: Array.isArray(room?.amenities) ? room.amenities : [],
  };
};

const ReserveContext = createContext(null);

export function ReserveProvider({ children, propertyId = 301676 }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rooms, setRooms] = useState([]);
  const [occupants, setOccupants] = useState({});

  // ✅ obligatorio: arranca vacío
  const [numGuests, setNumGuests] = useState("");

  const [pricesRules, setPriceRules] = useState(1);

  const [loading, setLoading] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [currency, setCurrency] = useState("ARS");
  const [mpPublicKey, setMpPublicKey] = useState(null);
  const [mpLocale, setMpLocale] = useState("es-AR");

  const resetSelection = useCallback(() => {
    setOccupants({});
    setRooms([]);
  }, []);

  const fetchRooms = useCallback(async () => {
    if (!startDate || !endDate) return;

    // ✅ obligatorio para buscar
    if (!numGuests) {
      alert("Seleccioná la cantidad de huéspedes para ver disponibilidad.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert("La fecha de inicio no puede ser posterior a la fecha de fin.");
      return;
    }

    try {
      setLoading(true);

      const body = { startDate, endDate, propertyId };
      const data = await getReserves(body);

      console.log("🔵 Respuesta getReserves:", data);

      if (Array.isArray(data?.data?.data)) {
        const rawRoomsFromApi = data.data.data;
        const roomsFromApi = rawRoomsFromApi.map(normalizeRoomFromApi);

        setRooms(roomsFromApi);

        const apiCurrency = (data?.data?.currency || "ARS").toUpperCase();
        const apiMpPublicKey = data?.data?.mpPublicKey || null;
        const apiMpLocale =
          data?.data?.mpLocale || (apiCurrency === "CLP" ? "es-CL" : "es-AR");

        setCurrency(apiCurrency);
        setMpPublicKey(apiMpPublicKey);
        setMpLocale(apiMpLocale);

        const apiPriceRules = data?.data?.pricesRules;
        if (Array.isArray(apiPriceRules) && apiPriceRules.length > 0) {
          setPriceRules(apiPriceRules[0]);
        } else {
          console.warn("⚠️ No llegaron pricesRules desde la API, uso fallback.");
        }

        const initialOccupants = {};
        roomsFromApi.forEach((room) => {
          initialOccupants[room.roomId] = 0;
        });
        setOccupants(initialOccupants);
      } else {
        console.error("⚠️ Estructura inesperada:", data?.data);
        setRooms([]);
        setOccupants({});
      }
    } catch (error) {
      console.error("❌ Error al obtener habitaciones:", error);
      setRooms([]);
      setOccupants({});
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, propertyId, numGuests]); // ✅ agrega numGuests

  const value = useMemo(
    () => ({
      propertyId,

      startDate,
      endDate,
      setStartDate,
      setEndDate,

      rooms,
      setRooms,
      occupants,
      setOccupants,
      numGuests,
      setNumGuests,

      pricesRules,
      setPriceRules,

      loading,
      setLoading,
      isOverlayVisible,
      setOverlayVisible,
      mobileOpen,
      setMobileOpen,

      currency,
      mpPublicKey,
      mpLocale,
      setCurrency,
      setMpPublicKey,
      setMpLocale,

      fetchRooms,
      resetSelection,
    }),
    [
      propertyId,
      startDate,
      endDate,
      rooms,
      occupants,
      numGuests,
      pricesRules,
      loading,
      isOverlayVisible,
      mobileOpen,
      currency,
      mpPublicKey,
      mpLocale,
      fetchRooms,
      resetSelection,
    ]
  );

  return (
    <ReserveContext.Provider value={value}>{children}</ReserveContext.Provider>
  );
}

export function useReserve() {
  const ctx = useContext(ReserveContext);
  if (!ctx)
    throw new Error("useReserve debe usarse dentro de <ReserveProvider />");
  return ctx;
}
